import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Button,
  Tooltip
} from '@mui/material';
import {
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { styled } from '@mui/material/styles';

const DashboardCard = styled(Paper)(({ theme, bgcolor }) => ({
  padding: { xs: '16px', sm: '20px' },
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: bgcolor,
  color: '#fff',
  borderRadius: '10px',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)'
  }
}));

const CardValue = styled(Typography)(({ theme }) => ({
  fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' },
  fontWeight: 'bold',
  marginTop: '10px',
  textAlign: 'center'
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: { xs: '0.95rem', sm: '1.1rem' },
  opacity: 0.9,
  textAlign: 'center'
}));

const IconWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 'auto',
  opacity: 0.4
});

export default function Dashboard() {
  const [resumo, setResumo] = useState({
    totalAtraso: 0,
    totalGeral: 0,
    capitalEmprestado: 0,
    totalAReceber: 0,
    totalPago: 0,
    totalJurosRecebidos: 0,
    clientesAtraso: 0,
    parcelasAtraso: 0,
    clientesAtrasados: [],
    jurosAplicados: 0
  });

  const [enviandoSMS, setEnviandoSMS] = useState({});
  const [modalSMS, setModalSMS] = useState({
    open: false,
    cliente: null,
    mensagem: ''
  });
  const [filtroNome, setFiltroNome] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    loadResumo();
  }, []);

  async function loadResumo() {
    try {
      // 1. Busca os dados gerais do dashboard
      const responseDashboard = await api.get('/dashboard');
      const resumo = responseDashboard.data;

      // 2. Busca a lista raw de clientes em atraso
      const responseRaw = await api.get('/dashboard/clientes-atrasados-raw');
      // Agrupa por cliente e soma o valor em atraso
      const clientesMap = new Map();
      responseRaw.data.forEach(parcela => {
        if (!clientesMap.has(parcela.cliente_id)) {
          clientesMap.set(parcela.cliente_id, {
            id: parcela.cliente_id,
            nome: parcela.nome_cliente,
            telefone: parcela.telefone,
            total_atraso: Number(parcela.valor),
            parcelas: [parcela]
          });
        } else {
          const cliente = clientesMap.get(parcela.cliente_id);
          cliente.total_atraso += Number(parcela.valor);
          cliente.parcelas.push(parcela);
        }
      });
      const clientesAtrasados = Array.from(clientesMap.values());

      // 3. Atualiza o resumo, mas sobrescreve a lista de clientes em atraso
      setResumo({
        ...resumo,
        clientesAtrasados,
        clientesAtraso: clientesAtrasados.length,
        parcelasAtraso: responseRaw.data.length,
        // CORREÇÃO: Usar o valor correto enviado pelo backend, não recalcular
        jurosAplicados: resumo.jurosAplicados || 0
      });
    } catch (error) {
      toast.error('Erro ao carregar dashboard');
      console.error(error);
    }
  }

  function prepararTextoSMS(cliente) {
    const parcelasAtraso = resumo.clientesAtrasados.find(c => c.id === cliente.id);
    if (!parcelasAtraso) return '';
  
    return `Olá ${cliente.nome}, você possui parcela(s) em atraso no valor total de ${formatCurrency(parcelasAtraso.total_atraso)}. Por favor, entre em contato para regularizar.`;
  }

  function abrirModalSMS(cliente) {
    setModalSMS({
      open: true,
      cliente,
      mensagem: prepararTextoSMS(cliente)
    });
  }

  function fecharModalSMS() {
    setModalSMS({
      open: false,
      cliente: null,
      mensagem: ''
    });
  }

  // Função para navegar para a página de Empréstimos com o cliente selecionado
  function abrirEmprestimos(cliente) {
    navigate('/emprestimos', { 
      state: { 
        filtroClienteId: cliente.id
      } 
    });
  }

  async function enviarCobranca() {
    const { cliente, mensagem } = modalSMS;
    if (!cliente || !mensagem.trim()) return;
  
    try {
      setEnviandoSMS(prev => ({ ...prev, [cliente.id]: true }));
      
      const response = await api.post('/sms/enviar', {
        mensagem: mensagem.trim(),
        cliente: {
          id: cliente.id,
          telefone: cliente.telefone
        }
      });
      
      toast.success('SMS enviado com sucesso!');
      fecharModalSMS();
      
      await loadResumo();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao enviar SMS');
      console.error('Erro ao enviar SMS:', error.response?.data);
    } finally {
      setEnviandoSMS(prev => ({ ...prev, [cliente.id]: false }));
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Filtro de clientes em atraso por nome
  const clientesAtrasadosFiltrados = resumo.clientesAtrasados.filter(cliente =>
    cliente.nome.toLowerCase().includes(filtroNome.toLowerCase())
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, pl: { xs: 1, md: 1.5 } }}>
      {/* Campo de filtro por nome */}
      <Box sx={{ mb: 2, maxWidth: 400 }}>
        <TextField
          label="Filtrar por nome do cliente"
          variant="outlined"
          size="small"
          fullWidth
          value={filtroNome}
          onChange={e => setFiltroNome(e.target.value)}
        />
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <DashboardCard bgcolor="#1976d2">
            <CardTitle variant="h6">Capital Emprestado</CardTitle>
            <CardValue>{formatCurrency(isNaN(resumo.capitalEmprestado) ? 0 : resumo.capitalEmprestado)}</CardValue>
            <IconWrapper>
              <MoneyIcon sx={{ fontSize: 40 }} />
            </IconWrapper>
          </DashboardCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <DashboardCard bgcolor="#ffa726">
            <CardTitle variant="h6">Total a Receber</CardTitle>
            <CardValue>{formatCurrency(resumo.totalAReceber)}</CardValue>
            <IconWrapper>
              <MoneyIcon sx={{ fontSize: 40 }} />
            </IconWrapper>
          </DashboardCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <DashboardCard bgcolor="#7e57c2">
            <CardTitle variant="h6">Juros Programados</CardTitle>
            <CardValue>{formatCurrency(resumo.jurosAplicados || 0)}</CardValue>
            <IconWrapper>
              <MoneyIcon sx={{ fontSize: 40 }} />
            </IconWrapper>
          </DashboardCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <DashboardCard bgcolor="#ef5350">
            <CardTitle variant="h6">Total em Atraso</CardTitle>
            <CardValue>{formatCurrency(resumo.totalAtraso)}</CardValue>
            <IconWrapper>
              <WarningIcon sx={{ fontSize: 40 }} />
            </IconWrapper>
          </DashboardCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <DashboardCard bgcolor="#ff9800">
            <CardTitle variant="h6">Clientes em Atraso</CardTitle>
            <CardValue>{resumo.clientesAtraso}</CardValue>
            <IconWrapper>
              <GroupIcon sx={{ fontSize: 40 }} />
            </IconWrapper>
          </DashboardCard>
        </Grid>
      </Grid>

      <Paper 
        sx={{ 
          mt: 4, 
          p: 0, 
          borderRadius: '10px',
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ bgcolor: '#f44336', color: 'white', p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon />
          <Typography variant="h6">
            Clientes em Atraso
          </Typography>
        </Box>

        {/* Tabela para desktop/tablet */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell align="right">Valor em Atraso</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientesAtrasadosFiltrados.map((cliente) => (
                  <TableRow key={cliente.id}>
                    <TableCell>{cliente.nome}</TableCell>
                    <TableCell>{cliente.telefone || 'Não cadastrado'}</TableCell>
                    <TableCell align="right" sx={{ color: '#ef5350', fontWeight: 'bold' }}>
                      {formatCurrency(cliente.total_atraso)}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Ligar" arrow>
                          <IconButton
                            color="primary"
                            size="small"
                            href={`tel:${cliente.telefone}`}
                            sx={{ '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' } }}
                          >
                            <PhoneIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="WhatsApp" arrow>
                          <IconButton
                            color="success"
                            size="small"
                            href={`https://wa.me/55${cliente.telefone?.replace(/\D/g, '')}?text=${encodeURIComponent(prepararTextoSMS(cliente))}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' } }}
                          >
                            <WhatsAppIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Email" arrow>
                          <IconButton
                            color="info"
                            size="small"
                            href={`mailto:${cliente.email}`}
                            sx={{ '&:hover': { backgroundColor: 'rgba(3, 169, 244, 0.1)' } }}
                          >
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="SMS" arrow>
                          <IconButton
                            color="secondary"
                            size="small"
                            onClick={() => abrirModalSMS(cliente)}
                            disabled={enviandoSMS[cliente.id]}
                            sx={{ '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.1)' } }}
                          >
                            {enviandoSMS[cliente.id] ? <CircularProgress size={20} /> : <SmsIcon />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Ver Empréstimo" arrow>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => abrirEmprestimos(cliente)}
                            sx={{ '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' } }}
                          >
                            <PaymentIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Cards para mobile */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5 }}>
          {clientesAtrasadosFiltrados.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography>Nenhum cliente em atraso</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {clientesAtrasadosFiltrados.map((cliente) => (
                <Paper
                  key={cliente.id}
                  elevation={1}
                  sx={{ p: 2, borderRadius: 1, border: '1px solid #e0e0e0' }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                    {cliente.nome}
                  </Typography>
                  
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Telefone:
                      </Typography>
                      <Typography variant="body2">
                        {cliente.telefone || 'Não cadastrado'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                        Valor em Atraso:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ef5350', fontWeight: 'bold', textAlign: 'right' }}>
                        {formatCurrency(cliente.total_atraso)}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', pt: 1 }}>
                    <IconButton
                      color="primary"
                      href={`tel:${cliente.telefone}`}
                      size="small"
                      sx={{ 
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                        border: '1px solid rgba(25, 118, 210, 0.3)',
                        borderRadius: 1
                      }}
                    >
                      <PhoneIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      color="success"
                      href={`https://wa.me/55${cliente.telefone?.replace(/\D/g, '')}?text=${encodeURIComponent(prepararTextoSMS(cliente))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ 
                        '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' },
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        borderRadius: 1
                      }}
                    >
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      color="info"
                      href={`mailto:${cliente.email}`}
                      size="small"
                      sx={{ 
                        '&:hover': { backgroundColor: 'rgba(3, 169, 244, 0.1)' },
                        border: '1px solid rgba(3, 169, 244, 0.3)',
                        borderRadius: 1
                      }}
                    >
                      <EmailIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      color="secondary"
                      onClick={() => abrirModalSMS(cliente)}
                      disabled={enviandoSMS[cliente.id]}
                      size="small"
                      sx={{ 
                        '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.1)' },
                        border: '1px solid rgba(156, 39, 176, 0.3)',
                        borderRadius: 1
                      }}
                    >
                      {enviandoSMS[cliente.id] ? <CircularProgress size={16} /> : <SmsIcon fontSize="small" />}
                    </IconButton>

                    <IconButton
                      color="primary"
                      onClick={() => abrirEmprestimos(cliente)}
                      size="small"
                      sx={{ 
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                        border: '1px solid rgba(25, 118, 210, 0.3)',
                        borderRadius: 1
                      }}
                    >
                      <PaymentIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      <Dialog 
        open={modalSMS.open} 
        onClose={fecharModalSMS}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WhatsAppIcon color="success" fontSize="medium" />
            <Typography variant="h6">Enviar Mensagem</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            margin="dense"
            label="Mensagem"
            fullWidth
            variant="outlined"
            value={modalSMS.mensagem}
            onChange={(e) => setModalSMS(prev => ({ ...prev, mensagem: e.target.value }))}
          />
          <Typography variant="caption" color="textSecondary">
            * A mensagem será enviada para o número {modalSMS.cliente?.telefone}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={fecharModalSMS} 
            color="inherit"
            sx={{ borderRadius: '20px' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={enviarCobranca} 
            variant="contained" 
            color="success"
            disabled={!modalSMS.mensagem.trim() || enviandoSMS[modalSMS.cliente?.id]}
            startIcon={enviandoSMS[modalSMS.cliente?.id] ? <CircularProgress size={20} color="inherit" /> : <WhatsAppIcon fontSize="small" />}
            sx={{ borderRadius: '20px' }}
          >
            {enviandoSMS[modalSMS.cliente?.id] ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
